import React, { useState } from 'react';
import {
    FileText,
    FileSpreadsheet,
    FileImage,
    File,
    Presentation,
    Download,
    Loader2,
} from 'lucide-react';
import { format } from './content.fallback';

/**
 * Download chips for files a reply generated.
 *
 * Rendered from the reply's files[] rather than parsed out of the answer text —
 * the model mentions generated files inconsistently, but the array is always
 * accurate.
 */

const ICON_BY_TYPE = [
    [/pdf/, FileText],
    [/(spreadsheet|excel|csv)/, FileSpreadsheet],
    [/(presentation|powerpoint|pptx?)/, Presentation],
    [/(word|document|docx?)/, FileText],
    [/image/, FileImage],
];

const iconFor = (mimeType = '') => {
    const match = ICON_BY_TYPE.find(([pattern]) => pattern.test(mimeType));
    return match ? match[1] : File;
};

const labelFor = (mimeType = '', filename = '') => {
    const ext = filename.includes('.')
        ? filename.split('.').pop().toUpperCase()
        : '';
    if (ext) return ext;
    return mimeType.split('/').pop().toUpperCase() || 'FILE';
};

const Attachments = ({ files, onDownload, copy }) => {
    // Track per-file so one slow download doesn't disable the others.
    const [pending, setPending] = useState({});

    if (!files?.length) return null;

    const handleDownload = async (filename) => {
        if (pending[filename]) return;

        setPending((prev) => ({ ...prev, [filename]: true }));
        try {
            await onDownload(filename);
        } finally {
            setPending((prev) => {
                const next = { ...prev };
                delete next[filename];
                return next;
            });
        }
    };

    return (
        <div className="sai-attachments">
            {files.map((file) => {
                const Icon = iconFor(file.mime_type);
                const isPending = Boolean(pending[file.filename]);

                return (
                    <button
                        key={file.filename}
                        type="button"
                        className="sai-attachment"
                        onClick={() => handleDownload(file.filename)}
                        disabled={isPending}
                        title={file.filename}
                    >
                        <span className="sai-attachment__icon">
                            <Icon size={16} aria-hidden="true" />
                        </span>
                        <span className="sai-attachment__body">
                            <span className="sai-attachment__name">{file.filename}</span>
                            <span className="sai-attachment__meta">
                                {labelFor(file.mime_type, file.filename)}
                            </span>
                        </span>
                        <span className="sai-attachment__action">
                            {isPending ? (
                                <Loader2 size={16} className="sai-spin" aria-hidden="true" />
                            ) : (
                                <Download size={16} aria-hidden="true" />
                            )}
                            <span className="sai-visually-hidden">
                                {format(
                                    isPending
                                        ? copy?.downloading_aria || 'Downloading {filename}'
                                        : copy?.download_aria || 'Download {filename}',
                                    { filename: file.filename }
                                )}
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

export default Attachments;
